import React, { useMemo, useState } from "react";
import classNames from "classnames";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";

import getPDFTranslations from "utils/translation/szc-pdf";
import { useStateSelector } from "utils/field";
import { getCalcPerformedBy } from "utils/user/getInfo";
import { defaultAttachedDocsNotes, getPDFDisplayData } from "utils/singleZoneCooling";

import Drawer from "components/Drawer";
import Button from "components/Button";
import Input from "components/Input";
import LangButton from "components/LangButton";
import PDF_Export_Document from "../Document";

import Export from "assets/images/icons/JSX/Export";
import classes from "./styles.module.scss";

const PDF_Export_Drawer = ({
    isPdfExportOpen,
    handleClose,
    results,
    displayUnits = "Metric",
}) => {
    /* Language Translation */
    const [lang, setLang] = useState("en");
    const { t } = getPDFTranslations(lang);

    /* Model and User Data */
    const { name: modelName = "" } = useStateSelector("model.modelDetails") || {};
    const userData = useStateSelector("user") || {};

    const calcPerformedBy = getCalcPerformedBy(userData) || {};

    /* Drawer Input Fields */
    const [inputFieldsData, setInputFieldsData] = useState({
        issuedForTheUseOf: "",
        projectNo: "",
        lot: "",
        site: "",
        email: calcPerformedBy?.email || "",
        attachedDocsNotes: [],
    });
    const [attachedDocsNotes, setAttachedDocsNotes] = useState(defaultAttachedDocsNotes);

    const handleChange = (value, name) => setInputFieldsData({ ...inputFieldsData, [name]: value });

    /* Display Data from Results */
    const displayData = useMemo(() => getPDFDisplayData(results, displayUnits, lang), [results, displayUnits, lang]);

    /* Exporting */
    const [exporting, toggleExporting] = useState(false);

    // Generate the report as a real vector PDF (US Letter) via @react-pdf/renderer.
    // No DOM/canvas rasterization — this replaces the html2canvas path that could
    // intermittently produce a black screen and oversized, non-standard pages.
    const handleExportPdf = async () => {
        toggleExporting(true);
        try {
            const blob = await pdf(
                <PDF_Export_Document
                    language={lang}
                    modelName={modelName}
                    inputFieldsData={inputFieldsData}
                    displayData={displayData}
                    calcPerformedBy={calcPerformedBy}
                    displayUnits={displayUnits}
                />
            ).toBlob();

            saveAs(blob, `${modelName}_cooling_zone_report_${lang}.pdf`);
        } catch (error) {
            console.error("Error generating PDF:", error);
        } finally {
            toggleExporting(false);
        }
    };

    return (
        <Drawer
            open={isPdfExportOpen}
            close={handleClose}
            title={t("SZCpdf.drawerTitle", "Export Cooling Zone Report [!!]")}
            subtitle={t(
                "SZCpdf.drawerInstructions",
                "Please provide the following information for your Cooling Zone report, and make sure to sign or stamp the bottom right area of the PDF. [!!]"
            )}
        >
            <div className={classNames(classes.inputRow, classes.firstInputRow)}>
                <Input
                    name={"email"}
                    label={t("SZCpdf.email", "E-mail [!!]")}
                    type={"text"}
                    input={{
                        value: inputFieldsData.email,
                        onChange: (value, name) => handleChange(value, name),
                    }}
                    className={classes.emailInput}
                />
                <LangButton lang={lang} setLang={setLang} />
            </div>
            <div className={classes.inputFields}>
                <div className={classes.inputRow}>
                    <Input
                        name={"issuedForTheUseOf"}
                        label={t("SZCpdf.issuedForTheUseOf", "Documents issued for the use of: [!!]")}
                        type={"text"}
                        input={{
                            value: inputFieldsData.issuedForTheUseOf,
                            onChange: (value, name) => handleChange(value, name),
                        }}
                        style={{ flex: 1 }}
                    />
                    <Input
                        name={"projectNo"}
                        label={t("SZCpdf.projectNo", "Project number [!!]")}
                        type={"text"}
                        input={{
                            value: inputFieldsData.projectNo,
                            onChange: (value, name) => handleChange(value, name),
                        }}
                        style={{ flex: 1 }}
                    />
                </div>
                <div className={classes.inputRow}>
                    <Input
                        name={"site"}
                        label={t("SZCpdf.buildingLocSite", "Building location - Site [!!]")}
                        type={"text"}
                        input={{
                            value: inputFieldsData.site,
                            onChange: (value, name) => handleChange(value, name),
                        }}
                        style={{ flex: 1 }}
                    />
                    <Input
                        name={"lot"}
                        label={t("SZCpdf.buildingLocLot", "Building location - Lot [!!]")}
                        type={"text"}
                        input={{
                            value: inputFieldsData.lot,
                            onChange: (value, name) => handleChange(value, name),
                        }}
                        style={{ flex: 1 }}
                    />
                </div>
            </div>
            <div className={classes.inputFields}>
                <div className={classes.inputLabel}>
                    {t("SZCpdf.additionalDocsList", "Other attached documents and notes [!!]")}
                </div>
                {attachedDocsNotes.map((docNote, index) => (
                    <div key={index} className={classes.inputRow}>
                        <div className={classNames(classes.inputLabel, classes.inputIndex)}>{index + 1}</div>
                        <Input
                            name={"attachedDocsNotes"}
                            type={"text"}
                            input={{
                                value: docNote,
                                onChange: (value) => {
                                    const newAttachedDocsNotes = [...attachedDocsNotes];
                                    newAttachedDocsNotes[index] = value;
                                    setAttachedDocsNotes(newAttachedDocsNotes);
                                },
                            }}
                            onBlurAction={() => handleChange(attachedDocsNotes, "attachedDocsNotes")}
                        />
                    </div>
                ))}
            </div>

            <div className={classes.pdfFormattingWarning}>
                <p>
                    {t(
                        "SZCpdf.warningPdfFormatting",
                        "If your PDF is not formatting properly, try restarting your browser."
                    )}
                </p>
            </div>

            <div className={classes.pdfBtnsContainer}>
                <Button type="hollow" onClick={handleClose} className={classes.cancelButton}>
                    {t("SZCpdf.cancel", "Cancel [!!]")}
                </Button>
                <Button icon={Export} onClick={handleExportPdf} disabled={exporting} className={classes.exportButton}>
                    {exporting
                        ? t("SZCpdf.exporting", "Exporting... [!!]")
                        : t("SZCpdf.exportPDF", "Export PDF [!!]")}
                </Button>
            </div>
        </Drawer>
    );
};

export default PDF_Export_Drawer;
